import React from 'react';

import { setPromiseTimeout } from '@/utils/setPromiseTimeout';

import {
  AnswerButton,
  AnswerButtonList,
} from '../../../InteractiveChat/components/AnswerButton';
import { MessageImage } from '../../../InteractiveChat/components/MessageImage';
import { MessagesList } from '../../../InteractiveChat/components/MessagesList';
import { MessageText } from '../../../InteractiveChat/components/MessageText';

const WAIT_BOT_QUESTION_TIME = 1000;

interface ChatScriptItemType {
  variant: 'received' | 'sent';
  content: NonNullable<React.ReactNode>;
  type: 'text' | 'image';
  answerVariants: string[];
}

const chatQuestionsScript: ChatScriptItemType[] = [
  {
    variant: 'received',
    content:
      'If I had you right here, right now… would you make me work for it, or just let me take what I want? 😏🔥',
    type: 'text',
    answerVariants: [
      'Why make you work when I want to be taken?',
      'You talk big – show me if you can back it up.',
    ],
  },
  {
    variant: 'received',
    content:
      'Damn… I like that answer. But don’t think I’ll go easy on you. 😈💪',
    type: 'text',
    answerVariants: ['Then quit talking and do something about it'],
  },
  {
    variant: 'received',
    content:
      'Careful what you ask for, baby… I might just give you exactly what you need. 😏🔥',
    type: 'text',
    answerVariants: [
      "Testing limits? I hope you're ready for what comes next..",
    ],
  },
];

export const ChatContent: React.FC<{
  onChatEnd: () => void;
}> = ({ onChatEnd }) => {
  const mountedRef = React.useRef<boolean>(false);

  const [messages, setMessages] = React.useState<
    Pick<ChatScriptItemType, 'content' | 'variant' | 'type'>[]
  >([]);
  const currentChatIndexRef = React.useRef<number>(0);
  const [answerVariants, setAnswerVariants] = React.useState<string[]>([]);
  const [showAnswerVariants, setShowAnswerVariants] =
    React.useState<boolean>(false);

  const showTyping = async () => {
    setMessages((prev) => [
      ...prev,
      {
        content: 'Typing...',
        variant: 'received',
        type: 'text',
      },
    ]);
  };

  const sendFinalMessage = () => {
    setMessages((prev) => [
      ...prev,
      {
        content: <MessageImage locked src="../assets/boy-message-img.png" />,
        variant: 'received',
        type: 'image',
      },
    ]);

    onChatEnd();
  };

  const askQuestion = async () => {
    /**
     * 1. set Typing... state to message
     * 2. wait WAIT_BOT_QUESTION_TIME
     * 3. update Typing... state with actual question
     * 4. set answers array with actual answers
     */
    showTyping();

    setPromiseTimeout(() => {
      setMessages((prev) => {
        prev.splice(prev.length - 1, 1, {
          content: chatQuestionsScript[currentChatIndexRef.current].content,
          variant: chatQuestionsScript[currentChatIndexRef.current].variant,
          type: 'text',
        });
        return prev;
      });

      const isLastQuestion =
        chatQuestionsScript.length - 1 === currentChatIndexRef.current;

      if (isLastQuestion) {
        sendFinalMessage();
      } else {
        setAnswerVariants(
          chatQuestionsScript[currentChatIndexRef.current].answerVariants,
        );

        // animation logic start
        setShowAnswerVariants(true);
        // animation logic end
      }
    }, WAIT_BOT_QUESTION_TIME);
  };

  const handleAnswerClick = async (content: string) => {
    /**
     * 1. add message to messages array
     * 2. empty answers array
     * 3. start waiting new message
     */

    // animation logic start
    setShowAnswerVariants(false);
    // animation logic end

    currentChatIndexRef.current = currentChatIndexRef.current + 1;
    setMessages((prev) => [
      ...prev,
      {
        content,
        variant: 'sent',
        type: 'text',
      },
    ]);
    setPromiseTimeout(() => {
      setAnswerVariants([]);
      askQuestion();
    }, WAIT_BOT_QUESTION_TIME);
  };

  React.useEffect(() => {
    if (!mountedRef.current) {
      askQuestion();
    }
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <MessagesList>
        {messages.map((message, index) => {
          if (message.type === 'image') {
            return message.content;
          }

          return (
            <MessageText
              key={index}
              content={message.content}
              variant={message.variant}
            />
          );
        })}
      </MessagesList>
      {answerVariants.length > 0 && (
        <AnswerButtonList>
          {answerVariants.map((text) => (
            <AnswerButton
              show={showAnswerVariants}
              key={text}
              onClick={() => {
                handleAnswerClick(text);
              }}
              text={text}
            />
          ))}
        </AnswerButtonList>
      )}
    </>
  );
};
